/**
 * Citation Display Helper
 * Generates properly formatted APA citations for display in cards and lists
 */

import { generateReference, formatNameForReference } from './citationGenerator';
import { mapBackendToFormFields, getFrontendSourceType } from './citationFieldMapper';

/**
 * Generate a formatted citation for display from backend citation data
 * @param {object} citation - Citation object from backend
 * @param {boolean} stripHtml - Whether to remove HTML tags (default: true for card display)
 * @returns {string} - Formatted APA citation
 */
export function generateCitationForDisplay(citation, stripHtml = true) {
	if (!citation) return '';

	try {
		const { category, subcategory, sourceType } = citation;

		// Get frontend source type for citation generator
		const backendSubcategory = subcategory || sourceType;
		const frontendSourceType = getFrontendSourceType(backendSubcategory, citation);

		// Transform backend data to form fields for citation generator
		const formFields = mapBackendToFormFields(citation);

		// Generate the full reference citation
		if (category && frontendSourceType) {
			let fullCitation = generateReference(category, frontendSourceType, formFields);

			// Remove HTML tags for card display if requested
			if (stripHtml) {
				fullCitation = fullCitation.replace(/<[^>]*>/g, '');
			}

			return fullCitation;
		}
	} catch (error) {
		console.error('Error generating citation for display:', error, citation);
	}

	// Fallback to basic citation format if generation fails
	return generateFallbackCitation(citation);
}

/**
 * Generate a simple fallback citation when proper generation fails
 * @param {object} citation - Citation object
 * @returns {string} - Basic citation format
 */
function generateFallbackCitation(citation) {
	const formatAuthors = (authorList) => {
		if (!authorList || authorList.length === 0) return 'Unknown Author';
		if (authorList.length === 1) return authorList[0];
		if (authorList.length === 2) return `${authorList[0]} & ${authorList[1]}`;
		const allButLast = authorList.slice(0, -1).join(', ');
		return `${allButLast}, & ${authorList[authorList.length - 1]}`;
	};

	const authors = formatAuthors(citation.authors || []);
	const year = citation.year || citation.date?.split(',')[0] || 'n.d.';
	const title = citation.title ||
		citation.chapterTitle ||
		citation.articleTitle ||
		citation.excerptOrPostTitle ||
		'Untitled';

	return `${authors} (${year}). ${title}.`;
}

/**
 * Get formatted author names for citation card header
 * Uses reference citation format with semicolons
 * Rules:
 * - 1 author: "LastName, F. M."
 * - 2 authors: "LastName1, F. M.; LastName2, F. M."
 * - 3+ authors: "LastName1, F. M. et al."
 * 
 * @param {object} citation - Citation object
 * @returns {string} - Formatted author names for card header
 */
export function getPrimaryAuthor(citation) {
	if (!citation) return 'Unknown Author';

	// Check if this is an organization citation - display organization name as-is
	if (citation.organization) {
		return citation.organization;
	}

	const { authors } = citation;

	if (!authors || authors.length === 0) {
		return 'Unknown Author';
	}

	const validAuthors = authors.filter((a) => a && a.trim());

	if (validAuthors.length === 0) return 'Unknown Author';

	// Format each author in reference style (LastName, F. M.)
	const formattedAuthors = validAuthors.map((author) => formatNameForReference(author));

	if (formattedAuthors.length === 1) {
		return formattedAuthors[0];
	} else if (formattedAuthors.length === 2) {
		// Use semicolon separator for 2 authors
		return `${formattedAuthors[0]}; ${formattedAuthors[1]}`;
	} else {
		// For 3+ authors, show first author with et al.
		return `${formattedAuthors[0]} et al.`;
	}
}

